import { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  getFilteredCustomers,
  getCustomersByBusinessAndType,
  getFilteredPendingAmount,
} from "../../database/helpers/customer";
import { NEW_SORT, SORT } from "../../constants";
import { handleApi } from "../../apis";
import { GET_SHARED_ACCOUNTS } from "../../apis/configuration";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export const useAllAccountsList = () => {
  const navigate = useNavigate();

  const [state, setState] = useState({
    isLoading: true,
    accountList: [],
    allAccountTypeCount: 0,
    pendingAmountaccountList: [],
    allNonFilterItemsCount: 0,
    totalCount: 0,
  });

  const cancelTokenSourceRef = useRef(null);

  const cancelPreviousRequest = () => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel(
        "Request canceled due to new request.",
      );
    }
    cancelTokenSourceRef.current = axios.CancelToken.source();
    return cancelTokenSourceRef.current;
  };

  const handleAccountList = async ({
    business,
    filters,
    limit,
    offset,
    isCalculate = true,
  }) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const isByOthers = filters?.customers?.toLowerCase() === "by others";

      const cancelToken = cancelPreviousRequest();

      let sort = "";
      if (filters.sort == NEW_SORT.amountHighToLow) {
        sort = "amountHighToLow";
      } else if (filters.sort == NEW_SORT.amountLowToHigh) {
        sort = "amountLowToHigh";
      } else {
        sort = filters?.sort?.toLowerCase();
      }

      if (isByOthers) {
        const params = {
          filters: {
            type: filters.type?.map((type) => type?.toLowerCase()),
            sort: sort,
            amount: filters?.amount?.min
              ? {
                  min: Number(filters?.amount?.min),
                  max: Number(filters?.amount?.max),
                }
              : {},
          },
          keyword: filters?.keywords,
          offset: isCalculate ? 0 : state.accountList.length,
        };

        const response = await handleApi(
          GET_SHARED_ACCOUNTS,
          params,
          cancelToken,
          navigate,
        );


        const updatedItems =
          offset > 0
            ? [...state.accountList, ...response?.data]
            : response?.data;

        setState((prev) => ({
          ...prev,
          isLoading: false,
          accountList: updatedItems,
          totalCount: response?.total_records,
          totalTake: response?.total_maine_diye || 0,
          totalGiven: response?.total_maine_liye || 0,
        }));

        return;
      }

      const businessId = business?.id || "";

      let totalTake = 0;
      let totalGiven = 0;
      let totalCount = state.totalCount;
      let allAccountTypeCount = state.allAccountTypeCount;

      if (isCalculate) {
        const allItems = await getFilteredCustomers(businessId, filters);

        const accountTypes = await getCustomersByBusinessAndType(
          businessId,
          filters.customers?.toLowerCase() === "suppliers",
        );

        allItems.forEach((item) => {
          const amount = item.total_outstanding_payment;

          if (amount > 0) totalGiven += amount;
          else if (amount < 0) totalTake += Math.abs(amount);
        });

        totalCount = allItems.length;
        allAccountTypeCount = accountTypes.length;
      }

      const paginatedItems = await getFilteredCustomers(
        businessId,
        filters,
        limit,
        offset,
      );

      const updatedItems =
        offset > 0 ? [...state.accountList, ...paginatedItems] : paginatedItems;

      setState((prev) => ({
        ...prev,
        isLoading: false,
        accountList: updatedItems,
        totalCount,
        totalTake: isCalculate ? totalTake : prev.totalTake,
        totalGiven: isCalculate ? totalGiven : prev.totalGiven,
        allAccountTypeCount,
      }));
    } catch (error) {
      if (!axios.isCancel(error)){
      setState((prev) => ({ ...prev, isLoading: false }));
      }
    }
  };

  const handleGetPendingAmountList = async ({
    business,
    filters,
    searchText,
    startPage,
    endPage,
  }) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    let start = moment(filters?.startDate || "20180101")
      .startOf("day")
      .valueOf();
    let end = moment(filters?.endDate || undefined)
      .endOf("day")
      .valueOf();
    let totalTake = 0;

    let items = await getFilteredPendingAmount(
      business?.id ? business.id : "",
      { searchText, start, end },
    );
    let allItems = await getFilteredPendingAmount(
      business?.id ? business.id : "",
      {
        searchText: "",
        start: "20180101",
        end: moment().endOf("day").valueOf(),
      },
    );

    await Promise.all(
      items.map(async (item) => {
        totalTake += item.total_outstanding_payment;
      }),
    );

    if (filters?.amount?.min || filters?.amount?.max) {
      const min = filters.amount.min ? Number(filters.amount.min) : null;
      const max = filters.amount.max ? Number(filters.amount.max) : null;
      items = items.filter((item) => {
        const amount = item.total_outstanding_payment || 0;
        if (min != null && !Number.isNaN(min) && amount < min) return false;
        if (max != null && !Number.isNaN(max) && amount > max) return false;
        return true;
      });
    }

    // Apply sorting to updated items
    const updatedItems = applySort(items, filters?.sort);
    // Paginate updated items
    const paginatedInvoiceItems = updatedItems.slice(startPage, endPage);
    setState((prev) => ({
      ...prev,
      isLoading: false,
      totalTake,
      pendingAmountaccountList: paginatedInvoiceItems,
      AllPendingAmountaccountListCount: items.length,
      allNonFilterItemsCount: allItems.length,
    }));
  };

  const applySort = (items, sort) => {
    switch (sort) {
      case SORT.amountHighToLow:
        return items.sort(
          (a, b) => b.total_outstanding_payment - a.total_outstanding_payment,
        );
      case SORT.amountLowToHigh:
        return items.sort(
          (a, b) => a.total_outstanding_payment - b.total_outstanding_payment,
        );
      case SORT.nameAToZ:
        return items.sort((a, b) =>
          a.name.toLowerCase() < b.name.toLowerCase()
            ? -1
            : a.name.toLowerCase() > b.name.toLowerCase()
              ? 1
              : 0,
        ); // Sorting by name A-Z
      case SORT.nameZToA:
        return items.sort((a, b) =>
          b.name.toLowerCase() < a.name.toLowerCase()
            ? -1
            : b.name.toLowerCase() > a.name.toLowerCase()
              ? 1
              : 0,
        ); // Sorting by name
      case SORT.Newest:
        return items.sort(
          (a, b) => b.last_transaction_at - a.last_transaction_at,
        ); // Default to sorting by creation date
      case SORT.Oldest:
        return items.sort(
          (a, b) => a.last_transaction_at - b.last_transaction_at,
        );
      default:
        return items.sort(
          (a, b) => b.last_transaction_at - a.last_transaction_at,
        );
    }
  };

    // Cancel any ongoing request when component unmounts
  useEffect(() => {
    return () => {
      cancelPreviousRequest();
    };
  }, []);

  return { ...state, handleAccountList, handleGetPendingAmountList };
};
